import { useEffect, useState } from 'react';
import { TrashIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import ConfirmModal from 'shared_items/ConfirmModal';
import DropdownWithInput from 'shared_items/DropdownWithInput';
import { createHealthProfessional, updateHealthProfessional, deleteHealthProfessional } from '../health_services';

const emptyForm = () => ({
  name: '',
  specialty: '',
  location: '',
  phone: '',
  address: '',
  crm: '',
  notes: '',
  is_active: true,
});

const formFromProfessional = (p) => ({
  name: p.name || '',
  specialty: p.specialty || '',
  location: p.location || '',
  phone: p.phone || '',
  address: p.address || '',
  crm: p.crm || '',
  notes: p.notes || '',
  is_active: p.is_active ?? true,
});

/**
 * Unified professional modal: create (professional=null), view + edit existing
 * (professional object). Same shell as ModalRecordForm / ModalTripForm.
 */
const ModalProfessionalForm = ({ professional, specialties = [], onClose, onSaved, onDeleted }) => {
  const isCreate = professional === null;
  const [isEditMode, setIsEditMode] = useState(isCreate);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setShowDeleteConfirm(false);
    if (isCreate) {
      setIsEditMode(true);
      setFormData(emptyForm());
    } else {
      setIsEditMode(false);
      setFormData(formFromProfessional(professional));
    }
  }, [professional]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSpecialtyChange = (value) => setFormData((prev) => ({ ...prev, specialty: value }));

  const handleEditToggle = () => {
    setFormData(formFromProfessional(professional));
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreate) {
      onClose();
    } else {
      setFormData(formFromProfessional(professional));
      setIsEditMode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setIsSubmitting(true);
    try {
      if (isCreate) {
        const response = await createHealthProfessional(formData);
        toast.success('Professional created successfully!');
        onSaved(response.data);
      } else {
        const response = await updateHealthProfessional(professional.id, formData);
        toast.success('Professional updated successfully!');
        onSaved(response.data);
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save professional');
      console.error('Failed to save health professional:', error.response?.data || error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setIsSubmitting(true);
    try {
      await deleteHealthProfessional(professional.id);
      toast.success('Professional deleted successfully!');
      onDeleted(professional.id);
      onClose();
    } catch (error) {
      toast.error('Failed to delete professional');
      console.error('Failed to delete health professional:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerTitle = isCreate ? 'Add Professional' : isEditMode ? 'Edit Professional' : 'Professional Details';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col z-10 mx-2 text-sm">

        {/* Header — compact, icon actions (matches ModalTripForm / ModalRecordForm) */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-800 pr-2 truncate leading-none">{headerTitle}</h2>
          <div className="flex items-center gap-0 flex-shrink-0">
            {!isCreate && !isEditMode && (
              <button type="button" onClick={handleEditToggle} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Edit">
                <PencilSquareIcon className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Close">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        {!isEditMode && !isCreate ? (
          <div className="flex-1 overflow-y-auto p-3 text-xs space-y-3">
            {professional.specialty && (
              <span className="inline-block px-3 py-1 bg-gray-700 text-white text-xs rounded-full">
                {professional.specialty}
              </span>
            )}
            <div>
              <span className="block font-medium text-gray-600">Location</span>
              <p className="text-gray-900 mt-0.5">{professional.location || '—'}</p>
            </div>
            <div>
              <span className="block font-medium text-gray-600">Phone</span>
              <p className="text-gray-900 mt-0.5">{professional.phone || '—'}</p>
            </div>
            <div>
              <span className="block font-medium text-gray-600">Address</span>
              <p className="text-gray-900 mt-0.5">{professional.address || '—'}</p>
            </div>
            <div>
              <span className="block font-medium text-gray-600">CRM</span>
              <p className="text-gray-900 mt-0.5">{professional.crm || '—'}</p>
            </div>
            <div>
              <span className="block font-medium text-gray-600">Notes</span>
              <p className="text-gray-800 whitespace-pre-line mt-0.5">{professional.notes?.trim() || 'No notes'}</p>
            </div>
            {!professional.is_active && (
              <span className="inline-block px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">Inactive</span>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
            <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Specialty</label>
                <DropdownWithInput
                  value={formData.specialty}
                  onChange={handleSpecialtyChange}
                  options={specialties}
                  placeholder="Type or select specialty"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Hospital, clinic, practice..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">CRM</label>
                  <input
                    type="text"
                    name="crm"
                    value={formData.crm}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  placeholder='e.g. "only accepts Bradesco", "avoid, bad experience"'
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              {!isCreate && (
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Active
                </label>
              )}
            </div>

            <div className="flex justify-between items-center gap-2 px-3 py-2 border-t border-gray-200 flex-shrink-0 bg-white">
              {!isCreate && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={isSubmitting}
                >
                  Delete
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        )}

        {showDeleteConfirm && !isSubmitting && (
          <ConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            title="Delete Professional"
            message="Are you sure you want to delete this professional? Records that reference it will keep their text but lose the link."
            confirmText="Delete"
            cancelText="Cancel"
            overlayClassName="z-[70]"
          />
        )}
      </div>
    </div>
  );
};

export default ModalProfessionalForm;
